import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  CheckCircle2, AlertCircle, BarChart3, TrendingUp, PieChart, 
  Settings, Smartphone, ArrowRight, ChevronDown, 
  ShieldCheck, Landmark, Search, Gauge, LayoutDashboard,
  Zap, Clock, Play, ArrowLeft, CreditCard, Sparkles
} from 'lucide-react';
import './LukosPremiumSolutions.css';
import { LeadCaptureModal } from '../LeadCaptureModal/LeadCaptureModal';
import { VideoModal } from '../VideoModal/VideoModal';

// --- Shared Components ---
const FeatureAccordion = ({ items, activeColor }: { items: any[], activeColor: string }) => {
  const [activeIndex, setActiveIndex] = useState(0);

  return (
    <div className="lps-accordion">
      {items.map((item, index) => (
        <div 
          key={index} 
          className={`lps-acc-item ${activeIndex === index ? 'active' : ''}`}
          style={{ borderColor: activeIndex === index ? activeColor : '#e2e8f0' }}
          onClick={() => setActiveIndex(index)}
        >
          <div className="lps-acc-header">
            <h3 style={{ color: activeIndex === index ? activeColor : '#0f172a' }}>{item.title}</h3>
            <ChevronDown size={20} className="lps-acc-icon" />
          </div>
          <AnimatePresence>
            {activeIndex === index && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="lps-acc-body"
              >
                <p>{item.description}</p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      ))}
    </div>
  );
};

// --- 1. CONCILIAÇÃO PREMIUM ---
export const LukosConciliacaoPremium = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const features = [
    { title: 'Auditoria de Taxas Automática', description: 'O Lukos confere cada centavo cobrado pelas adquirentes, garantindo que você nunca pague taxas acima do contratado.' },
    { title: 'Conciliação Bancária em 1 Clique', description: 'Diga adeus às planilhas. Sincronize seu extrato bancário com as vendas do PDV e identifique divergências instantaneamente.' },
    { title: 'Previsão de Recebíveis', description: 'Saiba exatamente quanto e quando o dinheiro vai cair na conta, permitindo um planejamento de fluxo de caixa sem sustos.' }
  ];

  return (
    <section className="lps-section" id="lukos-conciliacao-premium">
      <LeadCaptureModal 
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        productName="Conciliação Bancária"
        whatsappMessage="Olá! Gostaria de saber mais sobre a Conciliação Bancária."
      />
      <div className="lps-container">
        <div className="lps-hero">
          <div className="lps-hero-content">
            <div className="mb-8">
              <a href="/#solucoes-interativas" className="inline-flex items-center gap-2 text-sm font-bold text-slate-400 hover:text-slate-800 transition-colors">
                <ArrowLeft size={16} /> Voltar para visão geral
              </a>
            </div>
            <div className="lps-brand-tag">
              <img src="/logo.png" alt="Lukos" className="w-4 h-4 object-contain" />
              <span>Lukos</span> 
              <span className="lps-badge-green">Conciliação <ShieldCheck size={16} className="ml-1" /></span>
            </div>
            <h2 className="lps-title">Sua saúde financeira <span>sob controle total.</span></h2>
            <p className="lps-description">Elimine o erro humano e recupere lucros perdidos com a conciliação automatizada mais robusta do mercado.</p>
            <div className="lps-hero-actions">
              <button 
                onClick={() => setIsModalOpen(true)}
                className="lps-btn-primary shadow-xl"
              >
                Contratar serviço <ArrowRight size={18} />
              </button>
            </div>
          </div>

          <div className="lps-hero-visual order-2">
            <div className="lps-mockup-wrapper overflow-hidden">
              <img 
                src="/lukos_conciliacao_woman_premium.png" 
                alt="Conciliação Bancária Premium — Lukos Finance" 
                className="w-full h-full object-cover transition-transform duration-1000 hover:scale-110"
              />
              <div className="absolute inset-0 bg-linear-to-t from-[#0a0a0a] via-transparent to-transparent opacity-60"></div>
              <button className="lps-video-trigger shadow-2xl">
                <div className="w-9 h-9 bg-white rounded-full flex items-center justify-center text-[#0f172a] shadow-inner">
                  <Play size={16} fill="currentColor" />
                </div>
                <span>Ver vídeo 1 min</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// --- 2. LUKOS PAY PREMIUM ---
export const LukosPayPremium = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isInlineVideoPlaying, setIsInlineVideoPlaying] = useState(false);
  const features = [
    { title: 'Monitor de Volume por Bico', description: 'Acompanhe em tempo real a vazão de cada bico do seu posto e identifique padrões de consumo instantaneamente.' },
    { title: 'Ticket Médio e Margem Real', description: 'Visualize sua rentabilidade líquida descontando custos e taxas, sem precisar esperar o fechamento do mês.' },
    { title: 'Alertas de Estoque Crítico', description: 'Receba notificações automáticas quando o nível de um tanque atingir o limite de segurança, evitando falta de produto.' }
  ];

  return (
    <section className="lps-section bg-slate-50" id="lukos-pay-premium">
      <LeadCaptureModal 
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        productName="Lukos Pay / Dashboard"
        whatsappMessage="Olá! Gostaria de saber mais sobre o Lukos Pay."
      />
      <div className="lps-container">
        <div className="lps-hero lps-reverse">
          <div className="lps-hero-visual">
             <div className="lps-mockup-wrapper overflow-hidden bg-black">
               {!isInlineVideoPlaying ? (
                 <>
                   <img 
                     src="/lukos_pay_video_thumb.png" 
                     alt="Demonstração Lukos Pay" 
                     className="w-full h-full object-cover transition-transform duration-1000 hover:scale-110"
                   />
                   <div className="absolute inset-0 bg-linear-to-t from-[#0a0a0a] via-transparent to-transparent opacity-60"></div>
                   <button 
                     onClick={() => setIsInlineVideoPlaying(true)}
                     className="lps-video-trigger shadow-2xl"
                   >
                     <div className="w-9 h-9 bg-white rounded-full flex items-center justify-center text-[#0f172a] shadow-inner">
                       <Play size={16} fill="currentColor" />
                     </div>
                     <span>Ver vídeo 1 min</span>
                   </button>
                 </>
               ) : (
                 <iframe 
                   src="https://www.youtube.com/embed/sjWk3XpdH3s?autoplay=1&start=32"
                   title="YouTube video player"
                   frameBorder="0"
                   allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                   allowFullScreen
                   className="w-full h-full"
                 />
               )}
             </div>
          </div>

          <div className="lps-hero-content">
            <div className="mb-8">
              <a href="/#solucoes-interativas" className="inline-flex items-center gap-2 text-sm font-bold text-slate-400 hover:text-slate-800 transition-colors">
                <ArrowLeft size={16} /> Voltar para visão geral
              </a>
            </div>
            <div className="lps-brand-tag">
              <img src="/logo.png" alt="Lukos" className="w-4 h-4 object-contain" />
              <span>Lukos</span> 
              <span className="lps-badge-purple">Pay <CreditCard size={16} className="ml-1" /></span>
            </div>
            <h2 className="lps-title">A maquininha <span>inteligente do seu posto.</span></h2>
            <p className="lps-description">Pagamento e gestão em um único lugar. O Lukos Pay integra suas vendas de pista diretamente com o financeiro, eliminando erros e acelerando o seu fluxo de caixa.</p>
            <div className="lps-hero-actions">
              <button 
                onClick={() => setIsModalOpen(true)}
                className="lps-btn-primary shadow-xl"
              >
                Contratar serviço <ArrowRight size={18} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// --- 3. FIDELIDADE PREMIUM ---
export const LukosFidelidadePremium = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const features = [
    { title: 'Alteração Remota via Mobile', description: 'Mude os preços de venda diretamente pelo seu smartphone, de qualquer lugar, com segurança e rapidez.' },
    { title: 'Sincronização de Totem e Bombas', description: 'Atualize os preços no sistema e eles serão enviados automaticamente para os totens e concentradores em segundos.' },
    { title: 'Agendamento Inteligente', description: 'Programe mudanças de preços para horários estratégicos, automatizando promoções ou ajustes de mercado.' }
  ];

  return (
    <section className="lps-section" id="lukos-precos-premium">
      <LeadCaptureModal 
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        productName="Gestão de Preços"
        whatsappMessage="Olá! Gostaria de saber mais sobre a Gestão de Preços."
      />
      <div className="lps-container">
        <div className="lps-hero">
          <div className="lps-hero-content">
            <div className="mb-8">
              <a href="/#solucoes-interativas" className="inline-flex items-center gap-2 text-sm font-bold text-slate-400 hover:text-slate-800 transition-colors">
                <ArrowLeft size={16} /> Voltar para visão geral
              </a>
            </div>
            <div className="lps-brand-tag">
              <img src="/logo.png" alt="Lukos" className="w-4 h-4 object-contain" />
              <span>Lukos</span> 
              <span className="lps-badge-orange" style={{ background: 'linear-gradient(to right, #ec4899, #f43f5e)' }}>Fidelidade <Sparkles size={16} className="ml-1" /></span>
            </div>
            <h2 className="lps-title">Fidelidade que <span>gera lucro real.</span></h2>
            <p className="lps-description">Transforme clientes eventuais em fãs da sua marca. Com o Lukos Fidelidade, você oferece cashback automático e campanhas personalizadas que aumentam o ticket médio e a frequência na sua pista.</p>
            <div className="lps-hero-actions">
              <button 
                onClick={() => setIsModalOpen(true)}
                className="lps-btn-primary shadow-xl"
              >
                Contratar serviço <ArrowRight size={18} />
              </button>
            </div>
          </div>

          <div className="lps-hero-visual">
            <div className="lps-mockup-wrapper overflow-hidden bg-black shadow-2xl border-4 border-white/10">
              <iframe 
                src="https://www.youtube.com/embed/F_itCCQd0nk?start=32"
                title="YouTube video player"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                className="w-full h-full"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
